import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { ProductCard } from '../products/ProductCard';
import { CATEGORY_ICONS } from './FamilyCard';

export const ProductsDisplay = ({
    loading,
    allProducts,
    selectedCategory,
    categoriesWithProducts,
    productsByCategory,
    expandedCategories,
    onToggleCategory,
    onClearFilters,
}) => {
    if (loading) {
        return (
            <div className="space-y-8">
                {[1, 2, 3].map((i) => (
                    <div key={i}>
                        <div className="h-8 w-48 bg-jungle/10 animate-pulse rounded-lg mb-4"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((j) => (
                                <div key={j} className="aspect-[4/5] bg-jungle/5 animate-pulse rounded-2xl" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (allProducts.length === 0) {
        return (
            <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-jungle/10 flex items-center justify-center mb-4">
                    <Leaf className="w-8 h-8 text-jungle/50" />
                </div>
                <p className="text-muted-foreground text-lg">Aucun produit trouvé</p>
                <Button variant="link" onClick={onClearFilters} className="mt-2 text-leaf">
                    Retour aux familles
                </Button>
            </div>
        );
    }

    if (selectedCategory) {
        return (
            <div data-testid="products-grid">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {allProducts.map((product) => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-12" data-testid="products-by-category">
            {categoriesWithProducts.map((category) => (
                <div key={category.id} className="border-b border-jungle/10 pb-10 last:border-b-0">
                    <div className="mb-6">
                        <button
                            onClick={() => onToggleCategory(category.id)}
                            className="flex items-center gap-3 group w-full text-left"
                        >
                            <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                                style={{ backgroundColor: category.color + '15' }}
                            >
                                {CATEGORY_ICONS[category.id]}
                            </div>
                            <div className="flex-1">
                                <h2 className="font-heading text-2xl font-bold text-jungle group-hover:text-leaf transition-colors">
                                    {category.name}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {productsByCategory[category.id]?.length || 0} produits
                                </p>
                            </div>
                            {expandedCategories[category.id] ? (
                                <ChevronUp className="w-6 h-6 text-jungle/50" />
                            ) : (
                                <ChevronDown className="w-6 h-6 text-jungle/50" />
                            )}
                        </button>
                        <Link
                            to={`/catalogue?category=${category.id}`}
                            className="inline-flex items-center gap-1 text-sm text-leaf hover:underline mt-2 ml-16"
                        >
                            Voir uniquement cette catégorie
                            <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    {expandedCategories[category.id] && (
                        <div
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                            data-testid={`category-products-${category.id}`}
                        >
                            {productsByCategory[category.id]?.map((product) => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
